import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, FileText, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "../../App";

export default function DocumentsSection({ applicationId, documents, onDocumentUploaded }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post(`/applications/${applicationId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("common.documentUploaded"));
      onDocumentUploaded();
    } catch (error) {
      toast.error(t("common.failedToUpload"));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await api.get(`/documents/${docId}`);
      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("common.failedToDownload"));
    }
  };

  return (
    <Card className="p-6 bg-white border border-stone-200 shadow-sm">
      <h2 className="text-xl font-manrope font-bold text-emerald-700 mb-6">
        {t("documents.title")}
      </h2>

      <div className="mb-6">
        <Label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors"
          data-testid="upload-area"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Upload className="w-8 h-8 mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">
              {uploading ? t("documents.uploading") : t("documents.upload")}
            </p>
          </div>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
            data-testid="file-input"
          />
        </Label>
      </div>

      <div className="space-y-2" data-testid="documents-list">
        {documents.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            {t("documents.noDocuments")}
          </p>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 bg-stone-50 border border-stone-200 rounded-lg hover:bg-stone-100 transition-colors"
              data-testid={`document-${doc.id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileText className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-slate-900 truncate">{doc.filename}</p>
                  <p className="text-xs text-slate-500">
                    {format(new Date(doc.uploaded_at), "MMM dd, yyyy")}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                className="text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50 flex-shrink-0"
                data-testid={`download-${doc.id}`}
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
